// packages/front/src/portfolio/modals/add-symbol-modal.tsx

import { useEffect, useState, type FC, type FormEvent } from "react";
import { Button, Modal, Select } from "../../_common";
import { CURRENCY_LIST } from "@signals/shared";
import { useCreateSymbol, useUpdateSymbol } from "../../symbol";
import type { SymbolRecord } from "../portfolio.hooks";

export type { SymbolRecord } from "../portfolio.hooks";

export interface AddSymbolModalProps {
  open: boolean;
  onClose: () => void;
  editId?: string | undefined;
  initialData?: Partial<SymbolRecord> | undefined;
}

const TYPES = [
  "stock",
  "preferred_stock",
  "adr",
  "bond",
  "fund",
  "currency",
];

const inputClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";
const labelClass = "text-sm font-medium";

export const AddSymbolModal: FC<AddSymbolModalProps> = ({
  open,
  onClose,
  editId,
  initialData,
}) => {
  const isEdit = Boolean(editId);
  const createSymbol = useCreateSymbol();
  const updateSymbol = useUpdateSymbol();
  const saving = createSymbol.isPending || updateSymbol.isPending;

  const [symbol, setSymbol] = useState("");
  const [name, setName] = useState("");
  const [exchange, setExchange] = useState("");
  const [type, setType] = useState("stock");
  const [country, setCountry] = useState("");
  const [currency, setCurrency] = useState("");

  // Seed form from initialData when opening in edit mode
  useEffect(() => {
    if (!open || !initialData) return;
    if (initialData.symbol !== undefined) setSymbol(initialData.symbol);
    if (initialData.name !== undefined) setName(initialData.name ?? "");
    if (initialData.exchange !== undefined) setExchange(initialData.exchange ?? "");
    if (initialData.type !== undefined) setType(initialData.type ?? "stock");
    if (initialData.country !== undefined) setCountry(initialData.country ?? "");
    if (initialData.currency !== undefined) setCurrency(initialData.currency ?? "");
  }, [open, initialData]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      symbol,
      name: name || symbol,
      type,
      currency: currency || "USD",
      ...(exchange ? { exchange } : {}),
      ...(country ? { country } : {}),
    };
    try {
      if (isEdit && editId) {
        await updateSymbol.mutateAsync({ id: editId, body: payload });
      } else {
        await createSymbol.mutateAsync(payload);
      }
      onClose();
    } catch {
      // error available on mutation .error
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edit Symbol" : "Add Symbol"}>
      <p className="text-xs text-muted-foreground">
        {isEdit
          ? "Edit a Symbol record."
          : "Create a Symbol record manually. Use this for instruments not available via the datasource sync."}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <label className={labelClass}>Symbol (Ticker)</label>
        <input
          className={inputClass}
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          required
        />

        <label className={labelClass}>Name</label>
        <input
          className={inputClass}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className={labelClass}>Exchange</label>
        <input
          className={inputClass}
          value={exchange}
          onChange={(e) => setExchange(e.target.value)}
        />

        <label className={labelClass}>Type</label>
        <Select
          value={type}
          onChange={setType}
          options={TYPES.map((t) => ({ value: t, label: t }))}
          ariaLabel="Type"
        />

        <label className={labelClass}>Country</label>
        <input
          className={inputClass}
          value={country}
          onChange={(e) => setCountry(e.target.value)}
        />

        <label className={labelClass}>Currency</label>
        <Select
          value={currency}
          onChange={setCurrency}
          options={CURRENCY_LIST.map((c) => ({ value: c, label: c }))}
          placeholder="Select currency"
          ariaLabel="Currency"
        />

        <div className="mt-2 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
